import React, { useState, useEffect, useRef } from "react";
import {
  FiSave,
  FiTrash2,
  FiCheck,
  FiX,
  FiRefreshCw,
  FiXCircle,
  FiFacebook,
  FiAlertTriangle,
} from "react-icons/fi";
import { useNavigate, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getDatabase,
  ref,
  get,
  set,
  update,
  onValue,
} from "firebase/database";
import PointWarning from "./components/PointWarning";
import {
  POST_LENGTHS,
  POST_STYLES,
  DEFAULT_POST_LENGTH,
  DEFAULT_POST_STYLES,
} from "./constants/postSettings";
import PostLanguageSelector from "./components/PostLanguageSelector";
import ScheduleModeSelector from "./components/ScheduleModeSelector";
import FixedDateScheduleUI from "./components/FixedDateScheduleUI";
import { getLanguageName } from "./constants/languages";
// SDK import removed - uses backend API endpoints.
import ConfirmDialog from "./components/ConfirmDialog";
import ReviewQueuePanel from "./components/ReviewQueuePanel";
import SettingTooltip, {
  CONTACT_INFO_TOOLTIP_TEXT,
  FANPAGE_INFO_TOOLTIP_TEXT,
} from "./components/SettingTooltip";
import { buildFacebookAutoPostSettings } from "./utils/facebookAutoPostSettings";
import {
  PRODUCT_AUTO_POST_SOURCE_LOCAL,
  isWooProductAutoPostConfig,
  removePostConfigWithCronIndex,
  savePostConfigWithCronIndex,
} from "./utils/rtdbCronIndex";
import {
  getSocialAccountKey,
  loadConnectedSocialAccounts,
  normalizeSocialAccount,
  toLegacyFacebookFanpagePayload,
  toSelectedAccountPayload,
} from "./utils/socialChannels";

const getLockedConfigAccount = (config = {}) => {
  if (
    Array.isArray(config.selectedAccounts) &&
    config.selectedAccounts.length > 0
  ) {
    return normalizeSocialAccount(
      config.selectedAccounts[0],
      config.selectedAccounts[0]?.platform || config.platform || "facebook"
    );
  }

  if (config.fanpageID) {
    return normalizeSocialAccount(
      {
        platform: "facebook",
        accountId: config.fanpageID,
        id: config.fanpageID,
        name: config.fanpageName,
        access_token: config.accessToken,
      },
      "facebook"
    );
  }

  return null;
};

const ProductAutoPostGenerator = () => {
  const [userMembership, setUserMembership] = useState(null);
  const location = useLocation();
  const config = location.state?.config || {};
  const lockedConfigAccount = getLockedConfigAccount(config);
  const isAccountLocked = !!lockedConfigAccount;
  const [platform, setPlatform] = useState(
    config.platform || config.platformType || lockedConfigAccount?.platform || "facebook"
  );

  const [settings, setSettings] = useState({
    postLength: DEFAULT_POST_LENGTH,
    postStyles: DEFAULT_POST_STYLES,
    includeContactInfo: false,
    useFanpageInfo: false,
    autoPostEnabled: false,
    inputType: "products",
    allowNoImage: false,
    platformType: config.platformType || config.platform || "facebook",
    platform: config.platform || config.platformType || "facebook",
    selectedLanguage: "vi",
    customInfo: config.customInfo || config.prompt || "",
    firstCommentEnabled: config.firstCommentEnabled === true,
    defaultFirstCommentText: config.defaultFirstCommentText || "",
    fanpageProducts: [],
    scheduleMode: config.scheduleMode || "weekly",
    fixedDateSchedules: config.fixedDateSchedules || [],
  });

  const [requirements, setRequirements] = useState(config.prompt || "");
  const [productsList, setProductsList] = useState(config.products || "");
  const [fanpageProducts, setFanpageProducts] = useState(
    config.fanpageProducts || []
  );
  const [postLength, setPostLength] = useState(
    config.postLength || DEFAULT_POST_LENGTH
  );
  const [postStyles, setPostStyles] = useState(
    config.postStyles ? config.postStyles.split(", ") : DEFAULT_POST_STYLES
  );
  const [fanpages, setFanpages] = useState([]);
  const [selectedFanpages, setSelectedFanpages] = useState(
    lockedConfigAccount ? [lockedConfigAccount] : []
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedTimes, setSelectedTimes] = useState(
    config.times && config.times.length > 0 ? config.times : ["08:00"]
  );
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const [userPoint, setUserPoint] = useState(0);
  const [autoPostEnabled, setAutoPostEnabled] = useState(
    config.id ? (config.autoPostEnabled ?? false) : true
  );
  const [postMode, setPostMode] = useState(
    config.postMode || "direct"
  );
  const [includeContactInfo, setIncludeContactInfo] = useState(
    config.includeContactInfo || false
  );
  const [useFanpageInfo, setUseFanpageInfo] = useState(
    config.useFanpageInfo || false
  );
  const [allowNoImage, setAllowNoImage] = useState(
    config.allowNoImage || false
  );
  const [customInfo, setCustomInfo] = useState(
    config.customInfo || config.prompt || ""
  ); // Add customInfo state
  const [firstCommentEnabled, setFirstCommentEnabled] = useState(
    config.firstCommentEnabled === true
  );
  const [defaultFirstCommentText, setDefaultFirstCommentText] = useState(
    config.defaultFirstCommentText || ""
  );

  const [selectedDays, setSelectedDays] = useState(
    config.selectedDays || {
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: true,
      sunday: true,
    }
  );

  const [selectedLanguage, setSelectedLanguage] = useState("vi");

  const [topics, setTopics] = useState({});
  const [productPostingState, setProductPostingState] = useState(
    config.productPostingState &&
      typeof config.productPostingState === "object" &&
      !Array.isArray(config.productPostingState)
      ? config.productPostingState
      : {}
  );
  // Map productId -> isPosted (from /products/{userId}/{productId} node, source of truth for Facebook flow)
  const [productIsPostedMap, setProductIsPostedMap] = useState({});

  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Schedule mode states
  const [scheduleMode, setScheduleMode] = useState(
    config.scheduleMode || "weekly"
  );
  const [fixedDateSchedules, setFixedDateSchedules] = useState(
    config.fixedDateSchedules || []
  );

  // genAI removed - not used directly, all calls go via backend API

  const daysInVietnamese = {
    monday: "Thứ 2",
    tuesday: "Thứ 3",
    wednesday: "Thứ 4",
    thursday: "Thứ 5",
    friday: "Thứ 6",
    saturday: "Thứ 7",
    sunday: "Chủ nhật",
  };

  // Lấy thông tin membership của user
  // OPTIMIZATION: Added cleanup for Firebase listener to prevent memory leak
  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (userId) {
      const db = getDatabase();
      const membershipRef = ref(db, `/users/${userId}/membership`);

      const unsubscribe = onValue(membershipRef, (snapshot) => {
        if (snapshot.exists()) {
          setUserMembership(snapshot.val());
        }
      });

      return () => {
        unsubscribe();
      };
    }
  }, []);

  useEffect(() => {
    const loadFanpagesFromFirebase = async () => {
      if (localStorage.getItem("userId") == null) {
        navigate("/");
        return;
      }

      try {
        const db = getDatabase();
        const userId = localStorage.getItem("userId");
        const fanpagesFromFirebase = await loadConnectedSocialAccounts(db, userId, { platform });

        if (fanpagesFromFirebase.length > 0) {
          console.log("Accounts được tải từ Firebase:", fanpagesFromFirebase);
          setFanpages(fanpagesFromFirebase);

          if (config.selectedAccounts && Array.isArray(config.selectedAccounts)) {
            const selectedKeys = new Set(config.selectedAccounts.map(getSocialAccountKey));
            const selectedAccounts = fanpagesFromFirebase.filter((account) =>
              selectedKeys.has(getSocialAccountKey(account))
            );
            if (selectedAccounts.length > 0) {
              setSelectedFanpages(selectedAccounts);
            }
          } else if (lockedConfigAccount) {
            const selectedFanpage = fanpagesFromFirebase.find(
              (fanpage) =>
                getSocialAccountKey(fanpage) ===
                getSocialAccountKey(lockedConfigAccount)
            );
            if (selectedFanpage) {
              setSelectedFanpages([selectedFanpage]);
            }
          }
        } else {
          setFanpages([]);
          console.error("Không tìm thấy account Facebook/Zalo trong Firebase");
        }
      } catch (error) {
        console.error("Lỗi khi tải accounts từ Firebase:", error);
        toast.error("Có lỗi khi tải dữ liệu tài khoản. Vui lòng thử lại sau!", {
          position: "top-right",
          autoClose: 5000,
        });
      }
    };

    loadFanpagesFromFirebase();
  }, [navigate, config.fanpageID, config.selectedAccounts, platform]);

  // Realtime listener: track isPosted from product nodes
  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (!userId) return;

    const db = getDatabase();
    const productsRef = ref(db, `products/${userId}`);

    const unsubscribe = onValue(productsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const isPostedMap = {};
        Object.entries(data).forEach(([productId, product]) => {
          isPostedMap[productId] = product.isPosted === true;
        });
        setProductIsPostedMap(isPostedMap);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (config.id) {
      const db = getDatabase();
      const userId = localStorage.getItem("userId");

      // Nếu đến từ ProductsScreen, load config cụ thể theo ID
      if (isAccountLocked) {
        const configRef = ref(db, `posts/${userId}/${config.id}`);
        get(configRef)
          .then((snapshot) => {
            if (snapshot.exists()) {
              const data = { id: config.id, ...snapshot.val() };
              if (isWooProductAutoPostConfig(data)) {
                toast.error(
                  "Thiết lập WooCommerce cần chỉnh sửa ở màn Tự động đăng bài.",
                  {
                    position: "top-right",
                    autoClose: 3000,
                  }
                );
                navigate("/auto-post", { state: { config: data } });
                return;
              }

              // Giữ lại fanpageProducts từ location.state nếu có
              const currentFanpageProducts = config.fanpageProducts || [];
              loadSettingsFromData(data);
              // Ghi đè lại fanpageProducts với dữ liệu mới từ ProductsScreen
              if (currentFanpageProducts.length > 0) {
                setFanpageProducts(currentFanpageProducts);
              }
            }
          })
          .catch((error) => {
            console.error("Error fetching config:", error);
            toast.error("Không thể tải dữ liệu cấu hình!", {
              position: "top-right",
              autoClose: 3000,
            });
          });
      } else {
        // Không scan full posts để tránh download payload nặng.
        const configRef = ref(db, `posts/${userId}/${config.id}`);
        get(configRef)
          .then((snapshot) => {
            if (snapshot.exists()) {
              const data = { id: config.id, ...snapshot.val() };
              if (isWooProductAutoPostConfig(data)) {
                toast.error(
                  "Thiết lập WooCommerce cần chỉnh sửa ở màn Tự động đăng bài.",
                  {
                    position: "top-right",
                    autoClose: 3000,
                  }
                );
                navigate("/auto-post", { state: { config: data } });
                return;
              }

              loadSettingsFromData(data);
            }
          })
          .catch((error) => {
            console.error("Error fetching configs:", error);
            toast.error("Không thể tải dữ liệu cấu hình!", {
              position: "top-right",
              autoClose: 3000,
            });
          });
      }
    }
  }, [config.id, config.fanpageID, config.selectedAccounts]);

  // Helper to check if a product has been posted based on productPostingState
  const getProductPostStatus = (product, index, postingState) => {
    // Priority 1: Check isPosted from /products/{userId}/{productId} node (backend writes here for Facebook flow)
    const productId =
      product?.id !== undefined && product?.id !== null
        ? String(product.id).trim()
        : "";
    if (productId && productIsPostedMap[productId] === true) {
      return "posted";
    }

    // Priority 2: Check productPostingState from config node (for WooCommerce flow or detailed status)
    if (postingState && typeof postingState === "object") {
      const productName = String(product?.name || "").trim().toLowerCase();

      const candidateKeys = [];
      if (productId) {
        candidateKeys.push(
          `product_id_${productId}`,
          productId,
          `product_id_${encodeURIComponent(productId)}`
        );
      }
      if (productName) {
        candidateKeys.push(`product_name_${encodeURIComponent(productName)}`);
      }
      candidateKeys.push(`product_index_${index}`);

      for (const key of candidateKeys) {
        const state = postingState?.[key];
        if (state) {
          const status = String(state?.postStatus || "").toLowerCase();
          if (status) return status;
        }
      }
    }
    return null;
  };

  // Helper function để load settings từ data
  const loadSettingsFromData = (data) => {
    // Sanitize fanpageProducts to ensure no undefined values
    const sanitizedFanpageProducts = (data.fanpageProducts || []).map(
      (product) => ({
        ...product,
        name: product.name || "",
        description: product.description || "",
        price: product.price || 0,
        images: product.images || [],
        isActive: product.isActive !== undefined ? product.isActive : true,
        id: product.id || `product_${Date.now()}_${Math.random()}`,
      })
    );

    setRequirements(data.prompt || "");
    setProductsList(data.products || "");
    setFanpageProducts(sanitizedFanpageProducts);
    setPostLength(data.postLength || DEFAULT_POST_LENGTH);
    setPostStyles(
      data.postStyles ? data.postStyles.split(", ") : DEFAULT_POST_STYLES
    );
    setSelectedTimes(data.times ? data.times : [data.time] || ["00:00"]);
    setAutoPostEnabled(data.autoPostEnabled ?? false);
    setPostMode(data.postMode || "direct");
    setIncludeContactInfo(data.includeContactInfo || false);
    setUseFanpageInfo(data.useFanpageInfo || false);
    setAllowNoImage(data.allowNoImage || false);
    setCustomInfo(data.customInfo || data.prompt || ""); // Load customInfo from config
    setFirstCommentEnabled(data.firstCommentEnabled === true);
    setDefaultFirstCommentText(data.defaultFirstCommentText || "");

    // Load productPostingState from config data
    if (
      data.productPostingState &&
      typeof data.productPostingState === "object" &&
      !Array.isArray(data.productPostingState)
    ) {
      setProductPostingState(data.productPostingState);
    }

    // Load schedule mode with backward compatibility
    const loadedScheduleMode = data.scheduleMode || "weekly";
    setScheduleMode(loadedScheduleMode);

    // Load fixed date schedules if exists
    if (data.fixedDateSchedules) {
      setFixedDateSchedules(data.fixedDateSchedules);
    }

    const languageFromConfig = data.selectedLanguage;
    const languageFromLocalStorage = localStorage.getItem("selectedLanguage");
    const finalLanguage =
      languageFromConfig || languageFromLocalStorage || "vi";
    setSelectedLanguage(finalLanguage);

    setSettings({
      postLength: data.postLength || DEFAULT_POST_LENGTH,
      postStyles: data.postStyles
        ? data.postStyles.split(", ")
        : DEFAULT_POST_STYLES,
      includeContactInfo: data.includeContactInfo || false,
      useFanpageInfo: data.useFanpageInfo || false,
      autoPostEnabled: data.autoPostEnabled ?? false,
      inputType: "products",
      allowNoImage: data.allowNoImage || false,
      platformType: data.platformType || data.platform || "facebook",
      platform: data.platform || "facebook",
      selectedLanguage: finalLanguage,
      customInfo: data.customInfo || data.prompt || "",
      firstCommentEnabled: data.firstCommentEnabled === true,
      defaultFirstCommentText: data.defaultFirstCommentText || "",
      fanpageProducts: sanitizedFanpageProducts,
      scheduleMode: loadedScheduleMode,
      fixedDateSchedules: data.fixedDateSchedules || [],
    });

    if (data.platformType || data.platform) {
      setPlatform(data.platformType || data.platform || "facebook");
    }

    if (data.selectedAccounts && Array.isArray(data.selectedAccounts)) {
      setSelectedFanpages(data.selectedAccounts);
    } else if (data.selectedFanpages && Array.isArray(data.selectedFanpages)) {
      setSelectedFanpages(data.selectedFanpages);
    } else if (data.fanpageID && data.fanpageName) {
      setSelectedFanpages([
        {
          id: data.fanpageID,
          name: data.fanpageName,
          access_token: data.accessToken,
        },
      ]);
    }
  };

  const fetchUserPoints = async () => {
    try {
      const db = getDatabase();
      const userId = localStorage.getItem("userId");
      const pointsRef = ref(db, `users/${userId}/points`);
      const snapshot = await get(pointsRef);

      if (snapshot.exists()) {
        const data = snapshot.val();
        if (data.points !== undefined && data.purchasedPoints !== undefined) {
          setUserPoint(data.points + data.purchasedPoints);
        } else if (typeof data.points === "number") {
          setUserPoint(data.points);
        } else {
          console.error("Invalid points value:", data);
          setUserPoint(0);
        }
      } else {
        setUserPoint(0);
      }
    } catch (error) {
      console.error("Error fetching user points:", error);
      setUserPoint(0);
    }
  };

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (userId) {
      fetchUserPoints();
    }
  }, []);

  useEffect(() => {
    const loadUserSettings = async () => {
      try {
        const userId = localStorage.getItem("userId");
        if (!userId) return;

        const db = getDatabase();
        const settingsRef = ref(
          db,
          `users/${userId}/settings/productAutoPostGenerator`
        );
        const snapshot = await get(settingsRef);

        if (snapshot.exists()) {
          const loadedSettings = snapshot.val();

          // Sanitize fanpageProducts to ensure no undefined values
          const sanitizedFanpageProducts = (
            loadedSettings.fanpageProducts || []
          ).map((product) => ({
            ...product,
            name: product.name || "",
            description: product.description || "",
            price: product.price || 0,
            images: product.images || [],
            isActive: product.isActive !== undefined ? product.isActive : true,
            id: product.id || `product_${Date.now()}_${Math.random()}`,
          }));

          const languageFromFirebase = loadedSettings.selectedLanguage;
          const languageFromLocalStorage =
            localStorage.getItem("selectedLanguage");
          const finalLanguage =
            languageFromFirebase || languageFromLocalStorage || "vi";

          setSettings({
            ...loadedSettings,
            fanpageProducts: sanitizedFanpageProducts,
            firstCommentEnabled: loadedSettings.firstCommentEnabled === true,
            defaultFirstCommentText:
              loadedSettings.defaultFirstCommentText || "",
          });
          setSelectedLanguage(finalLanguage);

          setPostLength(loadedSettings.postLength || DEFAULT_POST_LENGTH);
          setPostStyles(loadedSettings.postStyles || DEFAULT_POST_STYLES);
          setIncludeContactInfo(loadedSettings.includeContactInfo || false);
          setUseFanpageInfo(loadedSettings.useFanpageInfo || false);
          setAutoPostEnabled(loadedSettings.autoPostEnabled ?? false);
          setPostMode(loadedSettings.postMode || "direct");
          setAllowNoImage(loadedSettings.allowNoImage || false);
          setCustomInfo(loadedSettings.customInfo || loadedSettings.prompt || "");
          setFirstCommentEnabled(loadedSettings.firstCommentEnabled === true);
          setDefaultFirstCommentText(
            loadedSettings.defaultFirstCommentText || ""
          );
          setFanpageProducts(sanitizedFanpageProducts);
          setScheduleMode(loadedSettings.scheduleMode || "weekly");
          setFixedDateSchedules(loadedSettings.fixedDateSchedules || []);
        } else {
          const languageFromLocalStorage =
            localStorage.getItem("selectedLanguage");
          const finalLanguage = languageFromLocalStorage || "vi";
          setSelectedLanguage(finalLanguage);
        }
      } catch (error) {
        console.error("Error loading settings:", error);
      }
    };

    if (!location.state?.config) {
      loadUserSettings();
    }
  }, [location.state]);

  const saveUserSettings = async (newSettings = null) => {
    try {
      const userId = localStorage.getItem("userId");
      if (!userId) return;

      const db = getDatabase();
      const settingsRef = ref(
        db,
        `users/${userId}/settings/productAutoPostGenerator`
      );

      const settingsToSave = newSettings || {
        ...settings,
        firstCommentEnabled: firstCommentEnabled,
        defaultFirstCommentText: defaultFirstCommentText,
        fanpageProducts: sanitizeFanpageProducts(fanpageProducts),
      };

      await set(settingsRef, settingsToSave);
      setSettings(settingsToSave);
    } catch (error) {
      console.error("Error saving settings:", error);
    }
  };

  const handleIncludeContactInfoChange = () => {
    const newValue = !includeContactInfo;
    setIncludeContactInfo(newValue);
    const newSettings = {
      ...settings,
      includeContactInfo: newValue,
    };
    saveUserSettings(newSettings);
  };

  const handleUseFanpageInfoChange = () => {
    const newValue = !useFanpageInfo;
    setUseFanpageInfo(newValue);
    const newSettings = {
      ...settings,
      useFanpageInfo: newValue,
    };
    saveUserSettings(newSettings);
  };

  const handlePostStyleChange = (style) => {
    const newStyles = postStyles.includes(style)
      ? postStyles.filter((s) => s !== style)
      : [...postStyles, style];
    setPostStyles(newStyles);
    const newSettings = {
      ...settings,
      postStyles: newStyles,
    };
    saveUserSettings(newSettings);
  };

  const handleAutoPostToggle = (e) => {
    const newValue = e.target.checked;
    setAutoPostEnabled(newValue);
    const newSettings = {
      ...settings,
      autoPostEnabled: newValue,
    };
    saveUserSettings(newSettings);
  };

  const handleLanguageChange = (language) => {
    setSelectedLanguage(language);
    localStorage.setItem("selectedLanguage", language);
    const newSettings = {
      ...settings,
      selectedLanguage: language,
    };
    saveUserSettings(newSettings);
  };

  const handleFirstCommentToggle = (e) => {
    const newValue = e.target.checked;
    setFirstCommentEnabled(newValue);
    const newSettings = {
      ...settings,
      firstCommentEnabled: newValue,
    };
    saveUserSettings(newSettings);
  };

  const handleDefaultFirstCommentTextChange = (e) => {
    const newValue = e.target.value;
    setDefaultFirstCommentText(newValue);
    const newSettings = {
      ...settings,
      defaultFirstCommentText: newValue,
    };
    saveUserSettings(newSettings);
  };

  const toggleDropdown = () => {
    // Nếu đến từ ProductsScreen với account cố định, không cho phép đổi account
    if (isAccountLocked) {
      return;
    }
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleFanpageSelect = (fanpage) => {
    // Nếu đến từ ProductsScreen với account cố định, không cho phép đổi account
    if (isAccountLocked) {
      return;
    }

    const targetKey = getSocialAccountKey(fanpage);
    const isAlreadySelected = selectedFanpages.some(
      (fp) => getSocialAccountKey(fp) === targetKey
    );

    if (isAlreadySelected) {
      // Bỏ chọn fanpage nếu đã được chọn
      setSelectedFanpages(
        selectedFanpages.filter((fp) => getSocialAccountKey(fp) !== targetKey)
      );
      console.log("Đã bỏ chọn account:", fanpage.name);
    } else {
      // Thêm fanpage vào danh sách đã chọn
      setSelectedFanpages([...selectedFanpages, fanpage]);
      console.log("Đã chọn thêm account:", fanpage.name);
    }
    // Không tự động đóng dropdown nữa - để người dùng click ra ngoài hoặc nhấn "Hoàn tất"
  };

  const handleDayChange = (day) => {
    setSelectedDays((prev) => ({
      ...prev,
      [day]: !prev[day],
    }));
  };

  const handlePlatformChange = (nextPlatform) => {
    // Không cho đổi platform khi đang edit config cũ hoặc account bị lock
    if (config.id || isAccountLocked) {
      return;
    }

    const normalizedPlatform = nextPlatform === "zalo" ? "zalo" : "facebook";
    setPlatform(normalizedPlatform);
    setSelectedFanpages([]);
    setIsDropdownOpen(false);

    if (normalizedPlatform === "zalo") {
      setPostMode("direct");
      setFirstCommentEnabled(false);
      setDefaultFirstCommentText("");
      setIncludeContactInfo(false);
    }
  };

  // Helper function để sanitize fanpageProducts trước khi lưu
  const sanitizeFanpageProducts = (products) => {
    return (products || []).map((product) => ({
      ...product,
      name: product.name || "",
      description: product.description || "",
      price: product.price || 0,
      images: product.images || [],
      isActive: product.isActive !== undefined ? product.isActive : true,
      id: product.id || `product_${Date.now()}_${Math.random()}`,
    }));
  };

  const saveAutoPostSettings = () => {
    if (isSaving) return;

    // Nếu không có account cố định (không đến từ ProductsScreen), kiểm tra account đã chọn
    if (!isAccountLocked && selectedFanpages.length === 0) {
      toast.error("Vui lòng chọn ít nhất 1 tài khoản!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    // Validate fixed-dates mode
    if (scheduleMode === "fixed-dates" && fixedDateSchedules.length === 0) {
      toast.error("Vui lòng thêm ít nhất một lịch đăng!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setIsSaving(true);

    const db = getDatabase();
    const userId = localStorage.getItem("userId");
    const promptSettings = buildFacebookAutoPostSettings({
      selectedFanpages,
      postLength,
      postStyles,
      customInfo,
      includeContactInfo,
      useFanpageInfo,
      selectedLanguage,
      postMode,
    });

    // Nếu có account cố định (đến từ ProductsScreen), chỉ lưu cho account đó
    if (isAccountLocked) {
      const fanpage = selectedFanpages[0] || lockedConfigAccount;
      const selectedAccount = toSelectedAccountPayload(fanpage);
      const platform = selectedAccount.platform || "facebook";
      const isFacebook = platform === "facebook";
      const legacyFanpages = isFacebook
        ? [toLegacyFacebookFanpagePayload(fanpage)]
        : [];
      const settingsToSave = {
        platform,
        productSource: PRODUCT_AUTO_POST_SOURCE_LOCAL,
        inputType: "products",
        prompt: "",
        products: productsList,
        time: null,
        times: selectedTimes,
        autoPostEnabled: autoPostEnabled,
        postMode: platform === "zalo" ? "direct" : promptSettings.postMode,
        userID: userId,
        postLength: promptSettings.postLength,
        postStyles: promptSettings.postStyles,
        includeContactInfo: isFacebook ? promptSettings.includeContactInfo : false,
        useFanpageInfo: promptSettings.useFanpageInfo,
        selectedDays: selectedDays,
        allowNoImage: allowNoImage,
        platformType: platform,
        selectedLanguage: promptSettings.selectedLanguage,
        customInfo: promptSettings.customInfo,
        promptProfileVersion: promptSettings.promptProfileVersion,
        firstCommentEnabled: isFacebook ? firstCommentEnabled : false,
        defaultFirstCommentText: isFacebook ? defaultFirstCommentText : "",
        scheduleMode: scheduleMode,
        ...(scheduleMode === "weekly" && {
          selectedDays: selectedDays,
          times: selectedTimes,
        }),
        ...(scheduleMode === "fixed-dates" && {
          fixedDateSchedules: fixedDateSchedules,
        }),
        selectedAccounts: [selectedAccount],
        selectedFanpages: legacyFanpages,
        accessToken: isFacebook ? fanpage.access_token || fanpage.accessToken || "" : "",
        fanpageID: isFacebook ? selectedAccount.accountId || "" : "",
        fanpageName: isFacebook ? selectedAccount.name || "" : "",
        imageURLs: "",
      };

      const saveOperation = savePostConfigWithCronIndex(
        db,
        userId,
        config.id,
        settingsToSave
      );

      saveOperation
        .then(() => {
          toast.success("Thiết lập đăng bài tự động đã được lưu!", {
            position: "top-right",
            autoClose: 3000,
          });

          if (!config.id) {
            setTimeout(() => {
              resetConfig();
            }, 1000);
          }
        })
        .catch((error) => {
          console.error("Failed to save settings:", error);
          toast.error("Lưu thiết lập thất bại!", {
            position: "top-right",
            autoClose: 3000,
          });
        })
        .finally(() => {
          setIsSaving(false);
        });
    } else {
      // Lưu settings cho từng account riêng biệt
      const savePromises = selectedFanpages.map((fanpage) => {
        const platform = fanpage.platform || "facebook";
        const accountId = fanpage.accountId || fanpage.id;
        const isFacebook = platform === "facebook";
        const selectedAccounts = [toSelectedAccountPayload(fanpage)];
        const legacyFanpages =
          isFacebook ? [toLegacyFacebookFanpagePayload(fanpage)] : [];
        const settingsToSave = {
          platform,
          productSource: PRODUCT_AUTO_POST_SOURCE_LOCAL,
          inputType: "products",
          prompt: "",
          products: productsList,
          time: null,
          times: selectedTimes,
          autoPostEnabled: autoPostEnabled,
          postMode: platform === "zalo" ? "direct" : promptSettings.postMode,
          userID: userId,
          postLength: promptSettings.postLength,
          postStyles: promptSettings.postStyles,
          includeContactInfo: isFacebook ? promptSettings.includeContactInfo : false,
          useFanpageInfo: promptSettings.useFanpageInfo,
          selectedDays: selectedDays,
          allowNoImage: allowNoImage,
          platformType: platform,
          selectedLanguage: promptSettings.selectedLanguage,
          customInfo: promptSettings.customInfo,
          promptProfileVersion: promptSettings.promptProfileVersion,
          firstCommentEnabled: platform === "zalo" ? false : firstCommentEnabled,
          defaultFirstCommentText:
            platform === "zalo" ? "" : defaultFirstCommentText,
          scheduleMode: scheduleMode,
          ...(scheduleMode === "weekly" && {
            selectedDays: selectedDays,
            times: selectedTimes,
          }),
          ...(scheduleMode === "fixed-dates" && {
            fixedDateSchedules: fixedDateSchedules,
          }),
          selectedAccounts,
          selectedFanpages: legacyFanpages,
          accessToken:
            isFacebook ? fanpage.access_token || fanpage.accessToken || "" : "",
          fanpageID: isFacebook ? accountId || "" : "",
          fanpageName: isFacebook ? fanpage.name || "" : "",
          imageURLs: "",

        };

        const saveOperation = savePostConfigWithCronIndex(
          db,
          userId,
          config.id ? `${config.id}_${platform}_${accountId}` : null,
          settingsToSave
        );

        return saveOperation;
      });

      Promise.all(savePromises)
        .then(() => {
          toast.success(
            `Thiết lập đăng bài tự động đã được lưu cho ${selectedFanpages.length} fanpage!`,
            {
              position: "top-right",
              autoClose: 3000,
            }
          );

          if (!config.id) {
            setTimeout(() => {
              resetConfig();
            }, 1000);
          }
        })
        .catch((error) => {
          console.error("Failed to save settings:", error);
          toast.error("Lưu thiết lập thất bại!", {
            position: "top-right",
            autoClose: 3000,
          });
        })
        .finally(() => {
          setIsSaving(false);
        });
    }
  };

  const resetConfig = () => {
    setRequirements("");
    setPostLength(DEFAULT_POST_LENGTH);
    setPostStyles(DEFAULT_POST_STYLES);
    setSelectedFanpages([]);
    setSelectedTimes([]);
    setTopics({});
    setCustomInfo(""); // Reset customInfo
    setFirstCommentEnabled(false);
    setDefaultFirstCommentText("");
    setPlatform("facebook"); // Reset platform về mặc định
    setSelectedDays({
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: true,
      sunday: true,
    });
    navigate("/products");
  };

  const deleteConfig = () => {
    if (!config.id) {
      toast.error("Không có thiết lập để xóa!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    // Hiển thị dialog xác nhận
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    const db = getDatabase();
    const userId = localStorage.getItem("userId");

    if (!config.id) {
      toast.error("Không có thiết lập để xóa!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    removePostConfigWithCronIndex(db, userId, config.id)
      .then(() => {
        toast.success("Đã xóa thiết lập thành công!", {
          position: "top-right",
          autoClose: 3000,
        });
        setTimeout(() => {
          resetConfig();
        }, 1000);
      })
      .catch((error) => {
        console.error("Failed to delete config:", error);
        toast.error("Xóa thiết lập thất bại!", {
          position: "top-right",
          autoClose: 3000,
        });
      });
  };

  const handleAllowNoImageChange = () => {
    const newValue = !allowNoImage;
    setAllowNoImage(newValue);
    const newSettings = {
      ...settings,
      allowNoImage: newValue,
    };
    saveUserSettings(newSettings);
  };

  // Thêm hàm xử lý customInfo
  const handleCustomInfoChange = (e) => {
    const newValue = e.target.value;
    setCustomInfo(newValue);
    const newSettings = {
      ...settings,
      customInfo: newValue,
    };
    saveUserSettings(newSettings);
  };



  // Derive current platform from locked account or explicit platform state
  const currentPlatform = isAccountLocked
    ? (lockedConfigAccount?.platform || platform)
    : platform;
  const isZaloPlatform = currentPlatform === "zalo";

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 pt-5">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4 sm:mb-6 md:mb-8 text-center">
          Thiết lập đăng bài tự động từ sản phẩm
        </h1>

        <PointWarning
          title="Vui lòng nạp thêm điểm để tiếp tục dùng chức năng này nhé ;)"
          userPoint={userPoint}
        />

        <div className="flex flex-col md:flex-row gap-6">
          {/* Phần bên trái - Cài đặt cơ bản */}
          <div className="w-full md:w-1/2">
            <div className="bg-white rounded-lg shadow-md p-5 mb-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-800">
                  Cài đặt sản phẩm
                </h2>
                <div className="flex items-center gap-2">
                  <a
                    href="https://youtu.be/boqoD2jfisY"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-medium rounded-md shadow-sm transition duration-300 hover:text-red-600"
                    title="Xem video hướng dẫn cách sử dụng"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 text-red-600"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" />
                    </svg>
                    <span>Hướng dẫn</span>
                  </a>
                </div>
              </div>

              <div className="mb-6">
                <div className="text-blue-700 mb-4">
                  Bài viết sẽ được tạo và đăng bài tự động lên {isZaloPlatform ? "Zalo OA" : "fanpage"} dựa trên
                  danh sách sản phẩm theo thứ tự đã được sắp xếp
                </div>

                {/* Hiển thị danh sách sản phẩm của fanpage */}
                {fanpageProducts.length > 0 && (
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-medium text-gray-800">
                        Danh sách sản phẩm ({fanpageProducts.length})
                      </h3>
                      {fanpageProducts.some((p) => productIsPostedMap[p.id]) && (
                        <button
                          onClick={async () => {
                            if (!window.confirm("Đặt lại trạng thái tất cả sản phẩm về chưa đăng?")) return;
                            const userId = localStorage.getItem("userId");
                            if (!userId) return;
                            const db = getDatabase();
                            const updates = {};
                            fanpageProducts.forEach((product) => {
                              if (product.id && productIsPostedMap[product.id]) {
                                updates[`products/${userId}/${product.id}/isPosted`] = false;
                              }
                            });
                            if (Object.keys(updates).length > 0) {
                              try {
                                await update(ref(getDatabase()), updates);
                                toast.success("Đã reset trạng thái tất cả sản phẩm!", {
                                  position: "top-right",
                                  autoClose: 2000,
                                });
                              } catch (err) {
                                console.error("Reset error:", err);
                                toast.error("Có lỗi khi reset trạng thái!", {
                                  position: "top-right",
                                  autoClose: 3000,
                                });
                              }
                            }
                          }}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-100 hover:bg-blue-100 hover:border-blue-200 rounded-md transition-all shadow-sm"
                        >
                          <FiRefreshCw className="w-4 h-4" />
                          <span>Đăng lại tất cả</span>
                        </button>
                      )}
                    </div>
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {fanpageProducts.map((product, index) => (
                        <div
                          key={product.id}
                          className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                        >
                          {/* Thông tin sản phẩm */}
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="text-sm font-medium text-blue-600 flex-shrink-0">
                                #{index + 1}
                              </span>
                              <h4 className="text-sm font-medium text-gray-900 truncate">
                                {product.name}
                              </h4>
                            </div>

                            <div className="flex items-center gap-2 flex-shrink-0">
                              {(() => {
                                const postStatus = getProductPostStatus(
                                  product,
                                  index,
                                  productPostingState
                                );
                                if (postStatus === "posted") {
                                  return (
                                    <div className="px-3 py-1 rounded-full text-xs font-medium bg-green-500 text-white">
                                      Đã đăng
                                    </div>
                                  );
                                }
                                if (postStatus === "in_progress") {
                                  return (
                                    <div className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                      ⏳ Đang xử lý
                                    </div>
                                  );
                                }
                                if (postStatus === "pending_review") {
                                  return (
                                    <div className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                      👁 Chờ duyệt
                                    </div>
                                  );
                                }
                                if (postStatus === "failed") {
                                  return (
                                    <div className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                      ❌ Thất bại
                                    </div>
                                  );
                                }
                                return null;
                              })()}

                              <div
                                className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  product.isActive
                                    ? "bg-green-100 text-green-800"
                                    : "bg-gray-100 text-gray-600"
                                }`}
                              >
                                {product.isActive ? "Đang chạy" : "Đã tắt"}
                              </div>
                            </div>
                          </div>

                          {/* Hình ảnh sản phẩm - hiển thị theo hàng ngang */}
                          {product.images && product.images.length > 0 ? (
                            <div className="flex gap-2 overflow-x-auto pb-1 mt-2" style={{ scrollbarWidth: 'thin' }}>
                              {product.images.map((image, imgIndex) => (
                                <img
                                  key={imgIndex}
                                  src={image}
                                  alt={`${product.name} ${imgIndex + 1}`}
                                  className="w-16 h-16 object-cover rounded-lg bg-gray-100 flex-shrink-0"
                                />
                              ))}
                            </div>
                          ) : (
                            <div className="flex mt-2">
                              <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-8 w-8 text-gray-400"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                  />
                                </svg>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  onClick={() => navigate("/product-create")}
                  className="inline-flex items-center justify-center bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm sm:text-base font-medium"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-2"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Thêm sản phẩm mới
                </button>
              </div>

              <div className="mb-6">
                <PostLanguageSelector
                  selectedLanguage={selectedLanguage}
                  onLanguageChange={handleLanguageChange}
                />
              </div>

              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">
                  Độ dài bài viết
                </label>
                <div className="flex w-full rounded-md shadow-sm">
                  {POST_LENGTHS.map(({ value, label }, index, arr) => (
                    <button
                      key={value}
                      type="button"
                      className={`flex-1 py-2 px-2 text-center text-sm font-medium
                        ${index === 0 ? "rounded-l-md" : ""}
                        ${index === arr.length - 1 ? "rounded-r-md" : ""}
                        ${
                          postLength === value
                            ? "bg-blue-600 text-white z-10"
                            : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                        }`}
                      onClick={() => setPostLength(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">
                  Phong cách bài viết
                </label>
                <div className="flex flex-wrap gap-2">
                  {POST_STYLES.map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      className={`py-2 px-4 rounded-full text-sm font-medium
                        ${
                          postStyles.includes(value)
                            ? "bg-blue-600 text-white"
                            : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                        }`}
                      onClick={() => handlePostStyleChange(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Info Field */}
              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">
                  Yêu cầu cá nhân hóa nội dung (không bắt buộc)
                </label>
                <textarea
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows="3"
                  value={customInfo}
                  onChange={handleCustomInfoChange}
                  placeholder="Nhập yêu cầu để cá nhân hóa nội dung bài viết. Ví dụ: Xưng hô tôi/bạn, nhắm đến khách hàng nữ 25-35 tuổi, nhấn mạnh lợi ích sức khỏe, tránh dùng từ ngữ quá chuyên môn, thêm câu hỏi tương tác cuối bài..."
                ></textarea>
                <p className="text-sm text-gray-500 mt-1">
                  💡 Mẹo: Mô tả về cách xưng hô, đối tượng khách hàng mục tiêu,
                  điểm nhấn nội dung, hoặc các yêu cầu đặc biệt khác
                </p>
              </div>

              <div className="mb-6">
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  {selectedFanpages.length < 2 && (
                    <div className="flex-1 p-3 border border-gray-200 rounded-md bg-white shadow-sm">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <label className="text-gray-700 font-medium">
                            {isZaloPlatform ? "Dùng thông tin Zalo OA" : "Dùng thông tin fanpage"}
                          </label>
                          <SettingTooltip
                            label={isZaloPlatform ? "Dùng thông tin Zalo OA" : "Dùng thông tin fanpage"}
                            text={FANPAGE_INFO_TOOLTIP_TEXT}
                          />
                        </div>
                        <div className="relative inline-block align-middle select-none shrink-0">
                          <input
                            type="checkbox"
                            name="fanpageInfo"
                            id="fanpageInfo"
                            checked={useFanpageInfo}
                            onChange={handleUseFanpageInfoChange}
                            className="hidden"
                          />
                          <label
                            htmlFor="fanpageInfo"
                            className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out cursor-pointer"
                            style={{
                              backgroundColor: useFanpageInfo
                                ? "#3b82f6"
                                : "#e5e7eb",
                            }}
                          >
                            <span
                              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition duration-200 ease-in-out ${
                                useFanpageInfo
                                  ? "translate-x-6"
                                  : "translate-x-1"
                              }`}
                            />
                          </label>
                        </div>
                      </div>
                    </div>
                  )}

                  {!isZaloPlatform && (
                  <div className="flex-1 p-3 border border-gray-200 rounded-md bg-white shadow-sm">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <label className="text-gray-700 font-medium">
                          Thêm thông tin liên hệ
                        </label>
                        <SettingTooltip
                          label="Thêm thông tin liên hệ"
                          text={CONTACT_INFO_TOOLTIP_TEXT}
                        />
                      </div>
                      <div className="relative inline-block align-middle select-none shrink-0">
                        <input
                          type="checkbox"
                          name="contactInfo"
                          id="contactInfo"
                          checked={includeContactInfo}
                          onChange={handleIncludeContactInfoChange}
                          className="hidden"
                        />
                        <label
                          htmlFor="contactInfo"
                          className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out cursor-pointer"
                          style={{
                            backgroundColor: includeContactInfo
                              ? "#3b82f6"
                              : "#e5e7eb",
                          }}
                        >
                          <span
                            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition duration-200 ease-in-out ${
                              includeContactInfo
                                ? "translate-x-6"
                                : "translate-x-1"
                            }`}
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Phần bên phải - Cài đặt lịch trình */}
          <div className="w-full md:w-1/2">
            <div className="bg-white rounded-lg shadow-md p-5 mb-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-800">
                  Cài đặt lịch trình
                </h2>
              </div>

              {/* Schedule Mode Selector */}
              <div className="mb-6">
                <ScheduleModeSelector
                  value={scheduleMode}
                  onChange={setScheduleMode}
                  disabled={isSaving}
                />
              </div>

              {/* Weekly Mode UI */}
              {scheduleMode === "weekly" && (
                <div className="mb-6">
                  <div className="flex flex-col md:flex-row gap-4">
                    <div className="w-full md:w-1/3 p-3 border border-gray-200 rounded-md bg-white shadow-sm">
                      <label className="block text-gray-700 font-medium mb-2">
                        Giờ đăng bài
                      </label>
                      <div className="space-y-3">
                        {selectedTimes.map((time, index) => {
                          const [hours, minutes] = time.split(":");
                          return (
                            <div
                              key={index}
                              className="flex items-center gap-2"
                            >
                              <div className="relative flex-1 flex shadow-sm rounded-md overflow-hidden border border-gray-200">
                                {/* Dropdown giờ */}
                                <div className="relative w-1/2">
                                  <select
                                    value={hours}
                                    onChange={(e) => {
                                      const newTimes = [...selectedTimes];
                                      const newHours = e.target.value;
                                      newTimes[
                                        index
                                      ] = `${newHours}:${minutes}`;
                                      setSelectedTimes(newTimes);
                                    }}
                                    className="w-full h-full px-4 py-2.5 text-gray-700 bg-white focus:ring-2 focus:ring-blue-500 border-none focus:outline-none appearance-none text-center font-medium"
                                  >
                                    {Array.from({ length: 24 }, (_, i) => {
                                      const hour = i < 10 ? `0${i}` : `${i}`;
                                      return (
                                        <option key={hour} value={hour}>
                                          {hour}
                                        </option>
                                      );
                                    })}
                                  </select>
                                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                                    <svg
                                      className="w-4 h-4 fill-current"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  </div>
                                </div>

                                {/* Dấu hai chấm */}
                                <div className="flex items-center justify-center bg-gray-50 px-2">
                                  <span className="text-gray-500 font-medium">
                                    :
                                  </span>
                                </div>

                                {/* Dropdown phút */}
                                <div className="relative w-1/2">
                                  <select
                                    value={minutes}
                                    onChange={(e) => {
                                      const newTimes = [...selectedTimes];
                                      const newMinutes = e.target.value;
                                      newTimes[
                                        index
                                      ] = `${hours}:${newMinutes}`;
                                      setSelectedTimes(newTimes);
                                    }}
                                    className="w-full h-full px-4 py-2.5 text-gray-700 bg-white focus:ring-2 focus:ring-blue-500 border-none focus:outline-none appearance-none text-center font-medium"
                                  >
                                    {(() => {
                                      const isDev = process.env.NODE_ENV === 'development';
                                      const step = isDev ? 1 : 5;
                                      const count = isDev ? 60 : 12;
                                      return Array.from({ length: count }, (_, i) => {
                                        const minute = i * step;
                                        const formattedMinute =
                                          minute < 10
                                            ? `0${minute}`
                                            : `${minute}`;
                                        return (
                                          <option
                                            key={formattedMinute}
                                            value={formattedMinute}
                                          >
                                            {formattedMinute}
                                          </option>
                                        );
                                      });
                                    })()}
                                  </select>
                                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                                    <svg
                                      className="w-4 h-4 fill-current"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  </div>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  const newTimes = selectedTimes.filter(
                                    (_, i) => i !== index
                                  );
                                  setSelectedTimes(newTimes);
                                }}
                                className={`p-2 text-white ${
                                  selectedTimes.length > 1
                                    ? "bg-red-500 hover:bg-red-600"
                                    : "bg-gray-300 cursor-not-allowed"
                                } rounded-full transition-colors focus:outline-none ${
                                  selectedTimes.length > 1
                                    ? "focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                                    : ""
                                }`}
                                aria-label="Xóa giờ đăng"
                                disabled={selectedTimes.length <= 1}
                                title={
                                  selectedTimes.length <= 1
                                    ? "Phải có ít nhất một giờ đăng bài"
                                    : "Xóa giờ đăng này"
                                }
                              >
                                <FiTrash2 size={16} />
                              </button>
                            </div>
                          );
                        })}
                        <button
                          type="button"
                          onClick={() =>
                            setSelectedTimes([...selectedTimes, "08:00"])
                          }
                          className="w-full px-4 py-2.5 bg-white text-blue-600 border border-blue-500 rounded-md hover:bg-blue-50 transition-colors duration-200 flex items-center justify-center font-medium shadow-sm"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 mr-2"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Thêm giờ đăng
                        </button>
                      </div>
                    </div>

                    <div className="w-full md:w-2/3 p-3 border border-gray-200 rounded-md bg-white shadow-sm">
                      <label className="block text-gray-700 font-medium mb-2">
                        Ngày đăng bài
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(daysInVietnamese).map(
                          ([day, label]) => (
                            <label
                              key={day}
                              className={`inline-flex items-center justify-center px-3 py-2 rounded-md cursor-pointer select-none transition-colors duration-200
                            ${
                              selectedDays[day]
                                ? "bg-blue-500 text-white"
                                : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                            }`}
                            >
                              <input
                                type="checkbox"
                                className="hidden"
                                checked={selectedDays[day]}
                                onChange={() => handleDayChange(day)}
                              />
                              <span className="text-sm font-medium">
                                {label}
                              </span>
                            </label>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Fixed Dates Mode UI */}
              {scheduleMode === "fixed-dates" && (
                <div className="mb-6">
                  <FixedDateScheduleUI
                    schedules={fixedDateSchedules}
                    onAdd={(schedule) => {
                      const newSchedule = {
                        ...schedule,
                        id: `schedule_${Date.now()}_${Math.random()
                          .toString(36)
                          .substr(2, 9)}`,
                        createdAt: new Date().toISOString(),
                        completed: false,
                      };
                      setFixedDateSchedules([
                        ...fixedDateSchedules,
                        newSchedule,
                      ]);
                      toast.success("Đã thêm lịch đăng mới!", {
                        position: "top-right",
                        autoClose: 3000,
                      });
                    }}
                    onEdit={(index, updates) => {
                      const updated = [...fixedDateSchedules];
                      updated[index] = { ...updated[index], ...updates };
                      setFixedDateSchedules(updated);
                      toast.success("Đã cập nhật lịch đăng!", {
                        position: "top-right",
                        autoClose: 3000,
                      });
                    }}
                    onDelete={(index) => {
                      setFixedDateSchedules(
                        fixedDateSchedules.filter((_, i) => i !== index)
                      );
                      toast.success("Đã xóa lịch đăng!", {
                        position: "top-right",
                        autoClose: 3000,
                      });
                    }}
                    onToggleCompleted={(index) => {
                      const updated = [...fixedDateSchedules];
                      updated[index] = {
                        ...updated[index],
                        completed: !updated[index].completed,
                        completedAt: !updated[index].completed
                          ? new Date().toISOString()
                          : null,
                      };
                      setFixedDateSchedules(updated);
                    }}
                  />
                </div>
              )}

              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">
                  Nền tảng đăng bài
                </label>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <button
                    type="button"
                    onClick={() => handlePlatformChange("facebook")}
                    disabled={!!config.id || isAccountLocked}
                    className={`flex items-center justify-center gap-2 border rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                      currentPlatform === "facebook"
                        ? "border-blue-500 bg-blue-50 text-blue-700"
                        : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
                    } ${config.id || isAccountLocked ? "cursor-not-allowed opacity-70" : ""}`}
                  >
                    <FiFacebook className="h-4 w-4" />
                    Facebook
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePlatformChange("zalo")}
                    disabled={!!config.id || isAccountLocked}
                    className={`flex items-center justify-center gap-2 border rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                      currentPlatform === "zalo"
                        ? "border-blue-500 bg-blue-50 text-blue-700"
                        : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
                    } ${config.id || isAccountLocked ? "cursor-not-allowed opacity-70" : ""}`}
                  >
                    <span className="inline-flex h-4 w-4 items-center justify-center rounded bg-blue-600 text-[10px] font-bold text-white">
                      Z
                    </span>
                    Zalo OA
                  </button>
                </div>
                {(config.id || isAccountLocked) && (
                  <p className="mb-4 text-xs text-gray-500">
                    Không đổi nền tảng khi chỉnh sửa để tránh trộn trạng thái
                    đăng giữa các tài khoản.
                  </p>
                )}

                <label className="block text-gray-700 font-medium mb-2">
                  {isZaloPlatform ? "Zalo OA đăng bài" : "Fanpage đăng bài"}
                </label>

                {/* Nếu có fanpageID từ config (đến từ ProductsScreen), hiển thị readonly */}
                {config.fanpageID ? (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        {isZaloPlatform ? (
                          <span className="text-blue-600 mr-3 font-bold text-sm">ZaloOA</span>
                        ) : (
                          <FiFacebook className="text-blue-600 mr-3" size={20} />
                        )}
                        <div>
                          <div className="font-medium text-gray-900">
                            {config.fanpageName || lockedConfigAccount?.name}
                          </div>
                          <div className="text-sm text-gray-600">
                            {isZaloPlatform ? "Zalo OA" : "Fanpage"} được chọn từ màn hình quản lý sản phẩm
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center text-blue-600">
                        <span className="text-sm font-medium mr-2">
                          ✓ Đã chọn
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Nếu không có config.fanpageID, hiển thị dropdown chọn tài khoản */
                  <>
                    {fanpages.length === 0 ? (
                      <button
                        onClick={() => {
                          navigate("/profile", {
                            state: { activeTab: isZaloPlatform ? "zalo" : "facebook" },
                          });
                          setTimeout(() => {
                            const element = document.getElementById(
                              isZaloPlatform ? "zalo-account-management" : "facebook-fanpage-management"
                            );
                            if (element) {
                              element.scrollIntoView({ behavior: "smooth" });
                            }
                          }, 500);
                        }}
                        className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm sm:text-base"
                      >
                        {isZaloPlatform ? "Thiết lập Zalo OA để đăng bài" : "Thiết lập fanpage để đăng bài"}
                      </button>
                    ) : (
                      <div ref={dropdownRef} className="relative">
                        <button
                          onClick={toggleDropdown}
                          className="w-full px-4 py-2 text-left border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          {selectedFanpages.length > 0
                            ? `${selectedFanpages.length} ${isZaloPlatform ? "Zalo OA" : "fanpage"} đã chọn`
                            : isZaloPlatform
                            ? "Chọn Zalo OA"
                            : "Chọn Fanpage"}
                        </button>
                        {isDropdownOpen && (
                          <div className="absolute z-50 w-full bg-white border border-gray-300 rounded-md mt-1 shadow-lg">
                            {/* Header - Ẩn nút chọn tất cả khi đến từ ProductsScreen */}
                            <div className="border-b border-gray-200 px-4 py-2 bg-gray-50">
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-700 font-medium">
                                  {config.fanpageID
                                    ? `${isZaloPlatform ? "Zalo OA" : "Fanpage"} được chọn (1/1)`
                                    : `Chọn ${isZaloPlatform ? "Zalo OA" : "fanpage"} (${selectedFanpages.length}/${fanpages.length})`}
                                </span>
                                {!config.fanpageID && (
                                  <button
                                    onClick={() => {
                                      if (
                                        selectedFanpages.length ===
                                        fanpages.length
                                      ) {
                                        // Nếu đã chọn tất cả thì bỏ chọn tất cả
                                        setSelectedFanpages([]);
                                      } else {
                                        // Nếu chưa chọn tất cả thì chọn tất cả
                                        setSelectedFanpages([...fanpages]);
                                      }
                                    }}
                                    className="text-sm text-blue-600 hover:text-blue-800 font-medium focus:outline-none"
                                  >
                                    {selectedFanpages.length === fanpages.length
                                      ? "Bỏ chọn tất cả"
                                      : "Chọn tất cả"}
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Danh sách fanpages */}
                            <div className="max-h-48 overflow-y-auto">
                              {fanpages.map((fanpage) => {
                                const isSelected = selectedFanpages.some(
                                  (fp) =>
                                    getSocialAccountKey(fp) ===
                                    getSocialAccountKey(fanpage)
                                );
                                return (
                                  <div
                                    key={getSocialAccountKey(fanpage)}
                                    onClick={() => handleFanpageSelect(fanpage)}
                                    className={`px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center justify-between ${
                                      isSelected ? "bg-blue-50" : ""
                                    }`}
                                  >
                                    <div className="flex items-center flex-1">
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => {}} // Handle by div onClick
                                        className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                      />
                                      <span className="text-left truncate">
                                        {fanpage.name}
                                      </span>
                                      <span className="ml-2 text-xs text-gray-500 uppercase">
                                        {fanpage.platform === "zalo"
                                          ? "Zalo OA"
                                          : "Facebook"}
                                      </span>
                                    </div>
                                    {isSelected && (
                                      <span className="text-blue-600 ml-2">
                                        ✓
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>

                            {/* Nút hoàn tất để đóng dropdown */}
                            <div className="border-t border-gray-200 px-4 py-3">
                              <button
                                onClick={() => setIsDropdownOpen(false)}
                                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm font-medium"
                              >
                                {config.fanpageID
                                  ? `Hoàn tất (1/1 ${isZaloPlatform ? "Zalo OA" : "fanpage"} đã chọn)`
                                  : `Hoàn tất (${selectedFanpages.length} ${
                                      isZaloPlatform ? "Zalo OA" : "fanpage"
                                    } đã chọn)`}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Hiển thị danh sách fanpages đã chọn */}
                    {selectedFanpages.length > 0 && (
                      <div className="mt-3">
                        <label className="block text-gray-700 font-medium mb-2">
                          {isZaloPlatform ? "Zalo OA" : "Fanpage"} đã chọn ({selectedFanpages.length}):
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {selectedFanpages.map((fanpage) => (
                            <div
                              key={getSocialAccountKey(fanpage)}
                              className="inline-flex items-center bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full"
                            >
                              <span className="mr-2">{fanpage.name}</span>
                              <button
                                onClick={() => handleFanpageSelect(fanpage)}
                                className="text-blue-600 hover:text-blue-800 focus:outline-none"
                                title={isZaloPlatform ? "Bỏ chọn Zalo OA này" : "Bỏ chọn fanpage này"}
                              >
                                <FiX size={16} />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="mb-6 border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <label className="block text-gray-700 font-medium">
                      Tự động comment đầu tiên
                    </label>
                    <p className="mt-1 text-sm text-gray-500">
                      Sau khi bài đăng thành công, hệ thống sẽ tự động comment
                      link hoặc nội dung follow-up.
                    </p>
                  </div>
                  <div className="relative inline-block align-middle select-none">
                    <input
                      type="checkbox"
                      name="firstCommentEnabled"
                      id="firstCommentEnabled"
                      checked={firstCommentEnabled}
                      onChange={handleFirstCommentToggle}
                      className="hidden"
                    />
                    <label
                      htmlFor="firstCommentEnabled"
                      className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out cursor-pointer"
                      style={{
                        backgroundColor: firstCommentEnabled
                          ? "#3b82f6"
                          : "#e5e7eb",
                      }}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition duration-200 ease-in-out ${
                          firstCommentEnabled
                            ? "translate-x-6"
                            : "translate-x-1"
                        }`}
                      />
                    </label>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-gray-700 font-medium mb-2">
                    Comment mặc định cho tất cả sản phẩm
                  </label>
                  <textarea
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows="3"
                    value={defaultFirstCommentText}
                    onChange={handleDefaultFirstCommentTextChange}
                    placeholder="Ví dụ: Link mua hàng / link affiliate / CTA muốn comment sau khi đăng bài"
                  ></textarea>
                  <p className="mt-1 text-sm text-gray-500">
                    Ưu tiên sử dụng comment riêng của từng sản phẩm trước. Nếu
                    sản phẩm không có comment riêng, hệ thống sẽ dùng nội dung
                    mặc định này.
                  </p>

                  {firstCommentEnabled && platform === "facebook" && selectedFanpages.filter(p => p.commentAutomationEligible !== true).length > 0 && (
                    <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start text-xs text-amber-800">
                      <span className="text-amber-500 mr-2 mt-0.5">⚠️</span>
                      <div>
                        <p className="font-medium mb-1">Thiếu quyền bình luận cho Fanpage:</p>
                        <p className="mb-2">
                          Fanpage: <strong>
                            {selectedFanpages.filter(p => p.commentAutomationEligible !== true).map((p) => p.name).join(", ")}
                          </strong> hiện chưa được cấp quyền bình luận. Bình luận đầu tiên có thể không hoạt động.
                        </p>
                        <a
                          href="/profile"
                          className="text-blue-600 hover:text-blue-800 font-semibold underline"
                        >
                          Cấp thêm quyền tại trang thiết lập Fanpage
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-6">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                  <label className="block text-gray-700 font-medium">
                    Tự động viết và đăng bài
                  </label>
                  <div className="relative inline-block align-middle select-none">
                    <input
                      type="checkbox"
                      name="autoPost"
                      id="autoPost"
                      checked={autoPostEnabled}
                      onChange={handleAutoPostToggle}
                      className="hidden"
                    />
                    <label
                      htmlFor="autoPost"
                      className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out cursor-pointer"
                      style={{
                        backgroundColor: autoPostEnabled
                          ? "#3b82f6"
                          : "#e5e7eb",
                      }}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition duration-200 ease-in-out ${
                          autoPostEnabled ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </label>
                  </div>
                </div>
                {autoPostEnabled ? (
                  <p className="mt-2 text-sm text-gray-600">
                    Hệ thống sẽ tự động viết và đăng bài theo thời gian đã cài
                    đặt
                  </p>
                ) : (
                  <p className="mt-2 text-sm text-gray-600">
                    Hiện tại tính năng tự động viết và đăng bài tự động đã tắt.
                    Vui lòng bật để sử dụng
                  </p>
                )}
              </div>

              {/* Chế độ đăng bài - hiện cho tất cả, disable nếu chưa có gói Chuyên nghiệp */}
              {autoPostEnabled && (
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <label className="block text-gray-700 font-medium">
                      Chế độ đăng bài
                    </label>
                    {userMembership < 2 && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">
                        🔒 Chuyên nghiệp
                      </span>
                    )}
                  </div>
                  <div className={`flex gap-2 ${userMembership < 2 ? "opacity-50 pointer-events-none" : ""}`}>
                    <button
                      type="button"
                      onClick={() => setPostMode("direct")}
                      className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg border-2 transition-all duration-200 text-sm font-medium ${
                        postMode === "direct"
                          ? "border-blue-500 bg-blue-50 text-blue-700"
                          : "border-gray-200 bg-white text-gray-500 hover:border-gray-300"
                      }`}
                    >
                      ⚡ Đăng ngay
                    </button>
                    <button
                      type="button"
                      onClick={() => setPostMode("review")}
                      className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg border-2 transition-all duration-200 text-sm font-medium ${
                        postMode === "review"
                          ? "border-amber-500 bg-amber-50 text-amber-700"
                          : "border-gray-200 bg-white text-gray-500 hover:border-gray-300"
                      }`}
                    >
                      👁 Duyệt trước
                    </button>
                  </div>
                  {userMembership < 2 ? (
                    <div className="mt-2 flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                      <span className="text-amber-600 text-sm">⬆️</span>
                      <p className="text-sm text-amber-700">
                        Nâng cấp lên gói <strong>Chuyên nghiệp</strong> để duyệt bài viết trước khi đăng.
                      </p>
                    </div>
                  ) : (
                    <p className="mt-2 text-sm text-gray-500">
                      {postMode === "direct"
                        ? `Bài viết sẽ được đăng trực tiếp lên ${isZaloPlatform ? "Zalo OA" : "Facebook"} ngay khi AI tạo xong.`
                        : "Bài viết sẽ được lưu vào hàng đợi để bạn duyệt trước khi đăng."}
                    </p>
                  )}
                </div>
              )}

              {/* Review Queue Panel */}
              <ReviewQueuePanel
                userId={localStorage.getItem("userId")}
                configId={config.id}
                isVisible={postMode === "review" && !!config.id && userMembership >= 2}
              />

              <div className="mt-6">
                <button
                  onClick={saveAutoPostSettings}
                  className={`flex items-center justify-center w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 text-sm sm:text-base font-medium ${
                    userPoint <= 0 || isSaving
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                  disabled={userPoint <= 0 || isSaving}
                >
                  {isSaving ? (
                    <>
                      <FiRefreshCw className="mr-2 h-5 w-5 animate-spin" />
                      Đang lưu...
                    </>
                  ) : (
                    <>
                      <FiSave className="mr-2 h-5 w-5" />
                      {userPoint <= 0
                        ? "Không đủ điểm"
                        : config.fanpageID
                        ? "Lưu thiết lập đăng bài tự động"
                        : `Lưu thiết lập cho ${selectedFanpages.length} ${isZaloPlatform ? "Zalo OA" : "fanpage"}`}
                    </>
                  )}
                </button>
                <div className="flex flex-col sm:flex-row gap-2 mt-2">
                  {config.id && (
                    <button
                      onClick={deleteConfig}
                      type="button"
                      className="flex items-center justify-center w-full bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors duration-200"
                    >
                      <FiTrash2 className="mr-2" />
                      Xóa thiết lập
                    </button>
                  )}
                  <button
                    onClick={resetConfig}
                    type="button"
                    className="flex items-center justify-center w-full bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                  >
                    Tạo thiết lập mới
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer />

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleConfirmDelete}
        title="Xác nhận xóa thiết lập"
        message="Bạn có chắc chắn muốn xóa thiết lập này? Hành động này không thể hoàn tác."
        confirmText="Xóa ngay"
        cancelText="Hủy bỏ"
        type="danger"
      />
    </div>
  );
};

export default ProductAutoPostGenerator;
